import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, MenuItem, Alert, CircularProgress, Grid, Typography, IconButton
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import {
  getJournalEntries, createJournalEntry, updateJournalEntryStatus,
  getChartOfAccounts
} from '../../../services/api';

const JournalEntries = () => {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    entry_type: 'manual',
    lines: [{ account_id: '', debit: '', credit: '', description: '' }, { account_id: '', debit: '', credit: '', description: '' }]
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [entriesRes, accountsRes] = await Promise.all([getJournalEntries(), getChartOfAccounts()]);
      setEntries(entriesRes.data || []);
      setAccounts((accountsRes.data || []).filter(a => !a.is_header));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: '', debit: '', credit: '', description: '' }] });

  const updateLine = (idx, field, value) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    setForm({ ...form, lines });
  };

  const removeLine = (idx) => {
    if (form.lines.length <= 2) return;
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    try {
      await createJournalEntry({
        ...form,
        lines: form.lines.map(l => ({
          account_id: parseInt(l.account_id),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description
        }))
      });
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id, status) => {
    try {
      await updateJournalEntryStatus(id, status);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const statusColor = { draft: 'default', pending: 'warning', approved: 'info', posted: 'success', reversed: 'error' };

  return (
    <ModuleHub moduleId="general-ledger" showFeatures={false}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>New Entry</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entry #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell><strong>{entry.entry_number}</strong></TableCell>
                  <TableCell>{entry.entry_date}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell align="right">{parseFloat(entry.total_debit).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                  <TableCell align="right">{parseFloat(entry.total_credit).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                  <TableCell><Chip label={entry.status} size="small" color={statusColor[entry.status] || 'default'} /></TableCell>
                  <TableCell>
                    {entry.status === 'draft' && (
                      <Button size="small" onClick={() => handleApprove(entry.id, 'posted')}>Post</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Journal Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></Grid>
            <Grid item xs={8}><TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Entry Lines</Typography>
          {form.lines.map((line, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
              <Grid item xs={5}>
                <TextField fullWidth select size="small" label="Account" value={line.account_id} onChange={(e) => updateLine(idx, 'account_id', e.target.value)}>
                  {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.account_code} - {a.account_name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={2}><TextField fullWidth size="small" type="number" label="Debit" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} /></Grid>
              <Grid item xs={2}><TextField fullWidth size="small" type="number" label="Credit" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} /></Grid>
              <Grid item xs={2}><TextField fullWidth size="small" label="Memo" value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => removeLine(idx)}><DeleteIcon /></IconButton></Grid>
            </Grid>
          ))}
          <Button size="small" onClick={addLine}>+ Add Line</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save Entry</Button>
        </DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default JournalEntries;
